import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef } from "react";

import { FlocknoteAppState } from "Flocknote/state.js";

import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";

import Portal from "Utilities/PortalMaker";

import "./FnrOverlay.scss";

const overlayAnimations = {
  variants: {
    initial: {
      scale: 0.5,
      opacity: 0,
      x: "-50%",
      y: "-50%",
    },
    open: {
      scale: 1,
      opacity: 1,
      x: "-50%",
      y: "-50%",
    },
    closed: {
      scale: 0.5,
      opacity: 0,
      x: "-50%",
      y: "calc(-50% + 100px)",
      transition: {
        type: "tween",
        duration: 0.3,
        ease: "backIn",
      },
    },
  },
  initial: "initial",
  animate: "open",
  exit: "closed",
};

FnrOverlay.propTypes = {
  componentName: PropTypes.string,
  show: PropTypes.bool,
  autoShow: PropTypes.bool,
  hideBackground: PropTypes.bool,
};

export default function FnrOverlay({
  children,
  componentName,
  show = false,
  autoShow = false,
  hideBackground = true,
  style,
  onOverlayHasAnimatedIn,
  onOverlayHasAnimatedOut,
}) {
  const stateDispatch = useContext(FlocknoteAppState);

  const overlayRef = useRef();

  const shouldBeShowing = useRef(show || autoShow);

  const overlayInfo = {
    ref: overlayRef,
    componentName: componentName,
    hideBackground: hideBackground,
  };

  useEffect(() => {
    if (autoShow) return;

    shouldBeShowing.current = show;

    stateDispatch[1]({
      type: show ? "Show an Overlay" : "Hide an Overlay",
      overlay: overlayInfo,
    });
    // eslint-disable-next-line
  }, [show]);

  useEffect(() => {
    if (!autoShow) return;

    stateDispatch[1]({
      type: "Show an Overlay",
      overlay: overlayInfo,
    });

    return () => {
      stateDispatch[1]({
        type: "Hide an Overlay",
        overlay: overlayInfo,
      });
    };
    // eslint-disable-next-line
  }, []);

  const animationComplete = () => {
    if (shouldBeShowing.current && onOverlayHasAnimatedIn)
      onOverlayHasAnimatedIn();
    else if (!shouldBeShowing.current && onOverlayHasAnimatedOut)
      onOverlayHasAnimatedOut();
  };

  return (
    <div className="FnrOverlayRef" ref={overlayRef}>
      <Portal to={document.getElementById("FnrOverlayHolder")}>
        {autoShow ? (
          // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
          // eslint-disable-next-line
          <FnrOverlayInside
            componentName={componentName}
            children={children}
            onAnimationComplete={animationComplete}
            style={style}
          />
        ) : (
          <AnimatePresence>
            {show && (
              <FnrOverlayInside
                componentName={componentName}
                // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
                // eslint-disable-next-line
                children={children}
                onAnimationComplete={animationComplete}
                style={style}
              />
            )}
          </AnimatePresence>
        )}
      </Portal>
    </div>
  );
}

function FnrOverlayInside({
  componentName,
  children,
  onAnimationComplete,
  style,
}) {
  return (
    <motion.div
      className={classNames(
        "FnrOverlay",
        componentName && "FnrOverlay_" + componentName
      )}
      onAnimationComplete={onAnimationComplete}
      style={style}
      {...overlayAnimations}
    >
      {children}
    </motion.div>
  );
}
